import React, { useState, useRef, useEffect } from 'react';
import { useTable, usePagination, useSortBy } from 'react-table';
import moment from 'moment';
import { FaEllipsisV } from 'react-icons/fa';

// Custom hook to detect clicks outside an element
function useOutsideAlerter(ref, callback) {
    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, callback]);
}

// The Cell renderer component for the actions dropdown
const ActionsCell = ({ row, onEdit, onDelete }) => {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);
    useOutsideAlerter(wrapperRef, () => setIsOpen(false));

    const handleDeleteClick = () => {
        if (window.confirm(`Are you sure you want to delete asset "${row.original.name}"?`)) {
            onDelete(row.original.id);
        }
        setIsOpen(false);
    };

    const handleEditClick = () => {
        onEdit(row.original);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <button
                type="button"
                className="inline-flex justify-center rounded-md px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                onClick={() => setIsOpen(!isOpen)}
            >
                <FaEllipsisV />
            </button>

            {isOpen && (
                <div 
                    className="fixed transform -translate-x-full mt-2 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                    style={{
                        top: wrapperRef.current.getBoundingClientRect().top + 30,
                        left: wrapperRef.current.getBoundingClientRect().right,
                    }}
                >
                    <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        <button
                            onClick={handleEditClick}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                            role="menuitem"
                        >
                            Edit
                        </button>
                        <button
                            onClick={handleDeleteClick}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 hover:text-red-800"
                            role="menuitem"
                        >
                            Delete
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const AssetTable = ({ data, totalPagesServer, currentPage2, pageSize2, onEdit, onDelete }) => {
    const columns = React.useMemo(
        () => [
            {
                Header: 'ID',
                accessor: 'id',
                width: '6%',
            },
            {
                Header: 'Name',
                accessor: 'name',
                width: '15%',
            },
            {
                Header: 'Category',
                accessor: 'category',
                width: '15%',
                Cell: ({ value }) => (
                    <span className="">{value}</span>
                )
            },
            {
                Header: 'Status',
                accessor: 'status',
                width: '7%',
                Cell: ({ value }) => (
                    <span className={`px-2 py-1 rounded-full text-sm ${
                        value === 'active' ? 'bg-green-100 text-green-800' :
                        value === 'inactive' ? 'bg-gray-100 text-gray-800' :
                        value === 'retired' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                    }`}>
                        {value.charAt(0).toUpperCase() + value.slice(1)}
                    </span>
                )
            },
            {
                Header: 'Location',
                accessor: 'location',
                width: '8%',
            },
            {
                Header: 'Sensitivity',
                accessor: 'sensitivity_level',
                width: '8%',
                Cell: ({ value }) => (
                    <span className={`px-2 py-1 rounded-full text-sm ${
                        value === 'public' ? 'bg-blue-100 text-blue-800' :
                        value === 'internal' ? 'bg-green-100 text-green-800' :
                        value === 'confidential' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                    }`}>
                        {value.charAt(0).toUpperCase() + value.slice(1)}
                    </span>
                )
            },
            {
                Header: 'Owner',
                accessor: 'owner_id',
                width: '10%',
                Cell: ({ row }) => (
                    <span>
                        {row.original.first_name && row.original.last_name 
                            ? `${row.original.first_name} ${row.original.last_name}`
                            : 'Unassigned'}
                    </span>
                )
            },
            {
                Header: 'Confidentiality',
                accessor: 'confidentiality',
                Cell: ({ value }) => (
                    <div className="relative inline-block">
                        <span className={`
        px-2 py-1 rounded-full text-sm whitespace-nowrap
        ${value === 'insignificant' ? 'bg-green-100 text-green-800' :
                                value === 'minor' ? 'bg-blue-100 text-blue-800' :
          /* …etc… */            'bg-red-100 text-red-800'
                            }
      `}>
                            {value ? value[0].toUpperCase() + value.slice(1) : 'Unassessed'}
                        </span>

                        <div
                            className="
          absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
          px-2 py-1 bg-gray-800 text-white text-xs rounded
          opacity-0 hover:opacity-100 transition-opacity duration-200
          whitespace-nowrap z-10
        "
                        >
                            {value ? value[0].toUpperCase() + value.slice(1) : 'Unassessed'}
                        </div>
                    </div>
                )
            },
            {
                Header: 'Integrity',
                accessor: 'integrity',
                width: '8%',
                Cell: ({ value }) => (
                    <div className="relative inline-block">
                        <span
                            className={`
          px-2 py-1 rounded-full text-sm whitespace-nowrap
          ${value === 'insignificant'
                                    ? 'bg-green-100 text-green-800'
                                    : value === 'minor'
                                        ? 'bg-blue-100 text-blue-800'
                                        : value === 'moderate'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : value === 'major'
                                                ? 'bg-orange-100 text-orange-800'
                                                : value === 'unassessed'
                                                    ? 'bg-gray-100 text-gray-800'
                                                    : 'bg-red-100 text-red-800'
                                }
        `}
                        >
                            {value
                                ? value.charAt(0).toUpperCase() + value.slice(1)
                                : 'Unassessed'}
                        </span>

                        <div
                            className="
          absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
          px-2 py-1 bg-gray-800 text-white text-xs rounded
          opacity-0 hover:opacity-100 transition-opacity duration-200
          whitespace-nowrap z-10
        "
                        >
                            {value
                                ? value.charAt(0).toUpperCase() + value.slice(1)
                                : 'Unassessed'}
                        </div>
                    </div>
                )
            },
            {
                Header: 'Availability',
                accessor: 'availability',
                className: 'normal-case',
                width: '8%',
                Cell: ({ value }) => (
                    <div className="relative inline-block">
                        <span
                            className={`
          px-2 py-1 rounded-full text-sm whitespace-nowrap
          ${value === 'insignificant'
                                    ? 'bg-green-100 text-green-800'
                                    : value === 'minor'
                                        ? 'bg-blue-100 text-blue-800'
                                        : value === 'moderate'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : value === 'major'
                                                ? 'bg-orange-100 text-orange-800'
                                                : value === 'unassessed'
                                                    ? 'bg-gray-100 text-gray-800'
                                                    : 'bg-red-100 text-red-800'
                                }
        `}
                        >
                            {value
                                ? value.charAt(0).toUpperCase() + value.slice(1)
                                : 'Unassessed'}
                        </span>

                        <div
                            className="
          absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
          px-2 py-1 bg-gray-800 text-white text-xs rounded
          opacity-0 hover:opacity-100 transition-opacity duration-200
          whitespace-nowrap z-10
        "
                        >
                            {value
                                ? value.charAt(0).toUpperCase() + value.slice(1)
                                : 'Unassessed'}
                        </div>
                    </div>
                )
            },
            // {
            //     Header: 'Last Updated',
            //     accessor: 'updated_at',
            //     width: '7%',
            //     Cell: ({ value }) => value ? moment(value).format('MM/DD/YYYY') : 'N/A'
            // },
            {
                Header: ' ',
                id: 'actions',
                width: '4%',
                Cell: ({ row }) => (
                    <div onClick={e => e.stopPropagation()}>
                        <ActionsCell
                            row={row}
                            onEdit={onEdit}
                            onDelete={onDelete}
                        />
                    </div>
                )
            }
        ],
        [onEdit, onDelete]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data: data || [],
            initialState: { pageIndex: 0 }
        },
        useSortBy,
        usePagination
    );

    return (
        // Outer container keeps its deep drop-shadow
        <div className="relative rounded-lg shadow-xl border border-gray-300">
            {/* Scroll container now has the main table shadow and rounding */}
            <div className="overflow-x-auto shadow-lg rounded-lg">
                <table
                    {...getTableProps()}
                    className="min-w-full bg-white"  // drop the shadow/rounding here
                >
                    <thead className="bg-gray-50">
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        className="px-6 py-3 text-left text-s font-medium text-gray-500 tracking-wider cursor-pointer whitespace-nowrap"
                                        style={{ width: column.width }}
                                    >
                                        {column.render('Header')}
                                        <span>
                                            {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody
                        {...getTableBodyProps()}
                        className="bg-white divide-y divide-gray-200"
                    >
                        {rows.map(row => {
                            prepareRow(row);
                            return (
                                <tr
                                    {...row.getRowProps({
                                        onClick: () => onEdit(row.original)
                                    })}
                                    className="group cursor-pointer transition-colors duration-200 hover:bg-gray-50"
                                >
                                    {row.cells.map(cell => (
                                        <td
                                            {...cell.getCellProps()}
                                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 transition-colors duration-200 group-hover:bg-gray-50"
                                        >
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AssetTable;
